export type ToastMenuItem = {
  name: string
  description?: string
  group?: string
  price: number
  soldOut?: boolean
}

export type ToastMenuSection = {
  id: string
  label: string
  items: ToastMenuItem[]
}

export const TOAST_MENU_LAST_COPIED = "2026-10-29"

// Snapshot of the Toast menu at order.toasttab.com/online/plus-bake-cafe-rancho-8685-base-line-road.
// Prices and names come from the Toast API (toast_get_menus); `soldOut` and the
// featured line-up come from the live ordering page, which is the only place
// 86/out-of-stock state is published.
//
// Names are normalized to fix Toast's typos ("Ethiopa", "Smores", "Vanilla  Latte",
// "Creme Brule Salt Bread");
// menu.tsx and coffee.ts reference these normalized names, so rename in all
// three together.
//
// Basque cheesecake is discontinued (2026-09-15) even though Toast's POS still
// lists it. Do not re-add it on a future sync.
//
// On 2026-10-07 Toast moved the croissants, cookies and muffins from its +BAKE
// menu into GUD2GO (+BAKE now holds only that cheesecake and POS-only
// pre-orders), so the bakery case sits under ON THE GO here too.
//
// On 2026-10-29 Toast dropped every individual croissant (Ivan confirmed: salt
// bread replaces them). Croissants survive only as "Croissants Assorted (1 dzn)",
// and the croissant sandwiches became salt bread sandwiches. Don't re-add
// single croissants unless Toast lists them again.
//
// On 2026-10-30 Aya (co-owner) confirmed there is no croissant dozen either,
// although Toast may still list "Croissants Assorted (1 dzn)". Leave it out.
//
// pages/menu.tsx indexes every item by name and keeps the FIRST match, so an
// entry repeated in "featured" shadows its canonical section. Keep repeated
// entries byte-identical to their canonical copy below.
export const TOAST_MENU_SECTIONS: ToastMenuSection[] = [
  {
    id: "featured",
    label: "FEATURED ITEMS",
    items: [
      {
        name: "Mexican Sidecar",
        description: "Espresso / horchata / cinnamon / whiskey brown sugar syrup.",
        group: "Featured Items",
        price: 6.5,
      },
      {
        name: "Longanisa Rice Bowl",
        description:
          "Grilled longanisa, white rice, scrambled egg, cucumber tomato salad, green onions, and fried garlic.",
        group: "Featured Items",
        price: 15.95,
      },
      {
        name: "Breakfast BurriTUS",
        description: "Bacon, eggs, twisties, cheese, tomato salsa, and a side of fries or salad.",
        group: "Featured Items",
        price: 12.95,
      },
    ],
  },
  {
    id: "food",
    label: "FOOD",
    items: [
      {
        name: "Avocado Bacon Toast",
        description:
          "Multigrain toast, avocado, bacon bits, over easy egg, arugula, and vinaigrette.",
        group: "FOOD",
        price: 13.95,
      },
      {
        name: "Belly 2 Belly Rice Bowl",
        description:
          "Grilled pork belly, Take Ur Seat signature sauce, steamed rice, cucumber tomato salad, scrambled egg, green onions, and crispy onions.",
        group: "FOOD",
        price: 15.95,
      },
      {
        name: "Belly On Fire Rice Bowl",
        description:
          "Roasted pork belly, steamed rice, cucumber tomato salad, scrambled egg, TUS hot sauce, green onions, and crispy onions.",
        group: "FOOD",
        price: 15.95,
      },
      {
        name: "Breakfast Sandwich",
        description:
          "Brioche bun, applewood smoked bacon, caramelized onions, scrambled eggs, provolone, arugula with vinaigrette, TUS spicy mayo, and fries.",
        group: "FOOD",
        price: 15.95,
      },
      {
        name: "Hot Chick Rice Bowl",
        description:
          "Steamed rice, pan fried chicken, cucumber tomato salad, scrambled egg, TUS hot sauce, green onions, and crispy onions.",
        group: "FOOD",
        price: 15.95,
      },
      {
        name: "Longanisa Rice Bowl",
        description:
          "Grilled longanisa, white rice, scrambled egg, cucumber tomato salad, green onions, and fried garlic.",
        group: "FOOD",
        price: 15.95,
      },
      {
        name: "RenDaang Beef Curry Bowl",
        description:
          "Steamed rice, rendang beef curry, scrambled egg, green chili, green onions, and crispy onions.",
        group: "FOOD",
        price: 17.95,
      },
      {
        name: "Spam Rice Bowl",
        description:
          "Steamed rice, grilled Spam, cucumber tomato salad, scrambled egg, TUS sauce, green onions, and crispy onions.",
        group: "FOOD",
        price: 15.95,
      },
      {
        name: "Sweet Chick Rice Bowl",
        description:
          "Steamed rice, pan fried chicken, cucumber tomato salad, scrambled egg, TUS sauce, green onions, and crispy onions.",
        group: "FOOD",
        price: 15.95,
      },
      {
        name: "Take Ur Seat Salad",
        description:
          "Romaine lettuce, spring mix, roasted red peppers, mandarin oranges, toasted almonds, and vinaigrette.",
        group: "FOOD",
        price: 10.95,
      },
      {
        name: "Tofu Mushroom Bowl",
        description:
          "Steamed rice, pan fried tofu, cremini mushroom, cucumber tomato salad, scrambled egg, TUS sauce, green onions, and crispy onions.",
        group: "FOOD",
        price: 15.95,
      },
      {
        name: "Breakfast BurriTUS",
        description: "Bacon, eggs, twisties, cheese, tomato salsa, and a side of fries or salad.",
        group: "FOOD",
        price: 12.95,
      },
      {
        name: "Spam Musubi",
        description: "Grilled Spam, furikake, rice, seaweed, and spam sauce.",
        group: "QUICK BITES",
        price: 4,
      },
      {
        name: "TUS Style Fries",
        description:
          "Cajun fries, caramelized grilled onions, TUS spicy mayo, and furikake.",
        group: "QUICK BITES",
        price: 12.95,
      },
      {
        name: "TUS Style Musubi",
        description:
          "Thick-cut grilled Spam, seasoned rice, seaweed, spam sauce, spicy mayo, and furikake.",
        group: "QUICK BITES",
        price: 8,
      },
      {
        name: "Twisties Fries",
        description: "Twisted potato wedges with Cajun fry seasoning.",
        group: "QUICK BITES",
        price: 5.95,
      },
      {
        name: "Fries",
        description: "Shoestring fries with Cajun fry seasoning.",
        group: "QUICK BITES",
        price: 5,
      },
      { name: "Side Avocado (1/2)", group: "SIDE ORDER", price: 4 },
      { name: "Side Cucumber Tomato Salad (4oz)", group: "SIDE ORDER", price: 3 },
      { name: "Side Eggs (2)", group: "SIDE ORDER", price: 6 },
      { name: "Side Multigrain Toast (1)", group: "SIDE ORDER", price: 2.5 },
      { name: "Side Protein", group: "SIDE ORDER", price: 8 },
      { name: "Side Spam (2)", group: "SIDE ORDER", price: 6 },
      { name: "Side Sauce", group: "SIDE ORDER", price: 1 },
      { name: "Side White Rice", group: "SIDE ORDER", price: 3.5 },
    ],
  },
  {
    id: "on-the-go",
    label: "ON THE GO",
    items: [
      { name: "Double Ube Salt Bread", group: "Salt Bread Sweet Series", price: 3.95 },
      { name: "Pandan Kaya Salt Bread", group: "Salt Bread Sweet Series", price: 3.95 },
      { name: "Almond Salt Bread", group: "Salt Bread Sweet Series", price: 3.95 },
      { name: "Pistachio Chocolate Salt Bread", group: "Salt Bread Sweet Series", price: 3.95 },
      { name: "Chocolate Salt Bread", group: "Salt Bread Sweet Series", price: 3.95 },
      { name: "Triple Chocolate Salt Bread", group: "Salt Bread Sweet Series", price: 3.95 },
      { name: "Creme Brulee Salt Bread", group: "Salt Bread Sweet Series", price: 3.95, soldOut: true },
      { name: "Blueberry Cream Cheese Salt Bread", group: "Salt Bread Sweet Series", price: 3.95 },
      { name: "Strawberry Custard Salt Bread", group: "Salt Bread Sweet Series", price: 3.95 },
      { name: "Butter Salt Bread", group: "Salt Bread Savories Series", price: 2.95 },
      { name: "Garlic Cream Cheese Salt Bread", group: "Salt Bread Savories Series", price: 3.95 },
      { name: "Japanese Sausage Salt Bread", description: "Japanese arabiki pork sausage on salt bread.", group: "Salt Bread Savories Series", price: 4.5 },
      { name: "Beef Salt Bread", group: "Salt Bread Savories Series", price: 3.95 },
      { name: "Corn Cheese Salt Bread", group: "Salt Bread Savories Series", price: 3.95 },
      { name: "Ham and Egg Salt Bread", group: "Salt Bread Savories Series", price: 4.95 },
      { name: "Turkey Provolone Salt Bread", group: "Salt Bread Savories Series", price: 4.95 },
      { name: "Bacon Egg Potato Salad Salt Bread", group: "Salt Bread Savories Series", price: 4.95 },
      { name: "Chicken Caprese Salt Bread", group: "Salt Bread Savories Series", price: 4.95, soldOut: true },
      { name: "Mini Chocolate Chip Cookies", description: "Bite-sized chocolate chip cookies.", group: "Cookies", price: 5 },
      { name: "Best Chocolate Chip Cookies", description: "Classic chocolate chip cookies with crisp edges and a soft center.", group: "Cookies", price: 4 },
      { name: "Pandan Biscoff Snickerdoodle", description: "Pandan snickerdoodle cookies with Biscoff crumbs.", group: "Cookies", price: 4 },
      { name: "Sea Salt Walnut Cookies", description: "Buttery cookies with walnuts, chocolate, and a sprinkle of sea salt.", group: "Cookies", price: 4 },
      { name: "S'mores Cookies", description: "S'mores cookie with marshmallows and chocolate chunks.", group: "Cookies", price: 4 },
      { name: "Pumpkin Cream Cheese Muffin", group: "Sweets", price: 4.5 },
      { name: "Banana Chocolate Chip Muffin", group: "Sweets", price: 4.5 },
      { name: "Longanisa Roll (3)", group: "Savories", price: 5.5, soldOut: true },
      { name: "Egg-Cellent Sando", description: "Soft milk toast, jammy egg, Kewpie mayo, salt, and pepper.", group: "Savories", price: 7.95 },
      { name: "Salted Butter Bread – 2 for $5.50", group: "Savories", price: 5.5, soldOut: true },
      { name: "Salted Butter Beef Bread – 2 for $7.50", group: "Savories", price: 7.5, soldOut: true },
      { name: "That OatLyfe", description: "Overnight oats, almond milk, chia seeds, brown sugar, strawberries, blueberries, bananas, honey, and toasted almonds.", group: "Chilled Cups", price: 6.95 },
      { name: "Berry Chia Pudding", description: "Chia seeds, pandan coconut milk, maple syrup, salt, strawberries, oranges, and blueberries.", group: "Chilled Cups", price: 6.95 },
      { name: "Ube Arroz con Leche", description: "Ube rice pudding served chilled.", group: "Chilled Cups", price: 5.95 },
      { name: "Arroz con Leche", description: "Rice pudding served chilled.", group: "Chilled Cups", price: 4.95 },
      { name: "Tiramisu Cup", group: "Chilled Cups", price: 4.95 },
      { name: "Ube Tiramisu Cup", group: "Chilled Cups", price: 5.95 },
      { name: "Belibis Hot Sauce Pouch", description: "Belibis hot sauce pouch 1KG.", group: "The Guds", price: 10 },
      // In-store only (POS/KIOSK, not on Toast online ordering).
      { name: "Belibis Hot Sauce Bottle", description: "Belibis hot sauce bottle, 18 fl oz.", group: "The Guds", price: 7 },
      // In-store only (POS), like the longanisa dozen below.
      { name: "Cookies Assorted (1 dzn)", group: "By the Dozen", price: 42 },
      { name: "Longanisa Roll (1 dzn)", group: "By the Dozen", price: 19 },
    ],
  },
  {
    id: "drinks",
    label: "DRINKS",
    items: [
      { name: "Babyccino", description: "Steamed milk / sprinkles / served warm.", group: "Specialty Latte", price: 3.5 },
      { name: "Chagaccino", description: "Espresso / mushroom coffee / milk / monk fruit sweetener.", group: "Specialty Latte", price: 7 },
      { name: "Cookie Butter Latte", description: "Espresso / milk / cookie butter syrup.", group: "Specialty Latte", price: 6.5 },
      { name: "Ghirardelli Hot Cocoa", description: "Ghirardelli cocoa / steamed milk.", group: "Specialty Latte", price: 5.5 },
      { name: "Houjicha Latte", description: "Houjicha / milk / simple syrup.", group: "Specialty Latte", price: 6 },
      { name: "Melon Latte + Foam", group: "Specialty Latte", price: 7.5 },
      { name: "Mexican Sidecar", description: "Espresso / horchata / cinnamon / whiskey brown sugar syrup.", group: "Specialty Latte", price: 6.5 },
      { name: "Milo Dinosaur", description: "Milo cocoa / milk / served over ice.", group: "Specialty Latte", price: 6 },
      { name: "Monk Fruit Vanilla Latte (sugar free)", group: "Specialty Latte", price: 6.5 },
      { name: "Pandan Latte", description: "Espresso / milk / pandan syrup.", group: "Specialty Latte", price: 6.5 },
      { name: "Spanish Latte", description: "Espresso / milk / honey / cinnamon. Our version is lightly sweet, made with honey instead of condensed milk.", group: "Specialty Latte", price: 6.5 },
      { name: "Sweet Latte", description: "Espresso / milk / brown sugar syrup.", group: "Specialty Latte", price: 5.5 },
      { name: "Vanilla Latte", group: "Specialty Latte", price: 6.5 },
      { name: "Choco Chip Latte", description: "Espresso / milk / choco chip syrup / chocolate chips.", group: "Specialty Latte", price: 7.5 },
      { name: "Banana Latte", description: "Espresso / milk / banana syrup / banana puree.", group: "Specialty Latte", price: 7.5 },
      { name: "Pumpkin Spice Latte", description: "Espresso / milk / pumpkin spice syrup / pumpkin puree.", group: "Specialty Latte", price: 7.5 },
      { name: "Matcha Latte", description: "Matcha / milk / simple syrup.", group: "Matcha Series", price: 5.95 },
      { name: "Blueberry Matcha", description: "Matcha / milk / blueberry compote.", group: "Matcha Series", price: 7.95 },
      { name: "Matchanana Latte", description: "Matcha / milk / banana.", group: "Matcha Series", price: 8.95 },
      { name: "Strawberry Matcha Latte", description: "Matcha / milk / strawberry compote / simple syrup.", group: "Matcha Series", price: 7.95 },
      { name: "Americano", description: "Double shot of Brazil Minas Gerais Luzia over hot water.", group: "Coffee", price: 4 },
      { name: "Cappuccino", description: "Espresso / steamed milk / deep foam.", group: "Coffee", price: 4.5 },
      { name: "Cold Brew", description: "Local Coffee Roasters beans, slow-steeped and served over ice.", group: "Coffee", price: 5.5 },
      { name: "Cortado", description: "Equal parts espresso and steamed milk.", group: "Coffee", price: 4 },
      { name: "Drip Coffee", description: "Batch brew of Local Coffee Roasters beans, roasted weekly in Chino Hills.", group: "Coffee", price: 4.5 },
      { name: "Espresso", description: "Double shot of Brazil Minas Gerais Luzia — caramel, chocolate, cream.", group: "Coffee", price: 3.5 },
      { name: "Espresso Tonic", description: "Espresso over tonic water and ice — bright and sparkling.", group: "Coffee", price: 5.25 },
      { name: "Latte", description: "Double shot and steamed milk.", group: "Coffee", price: 5 },
      { name: "Bottled Drinks", group: "Tea & Juice", price: 4 },
      { name: "Fresh Squeezed Orange Juice", group: "Tea & Juice", price: 6 },
      { name: "Hot Tea by Republic of Tea", description: "By Republic of Tea.", group: "Tea & Juice", price: 4 },
      { name: "Iced Pandan Jasmine Sweetea", group: "Tea & Juice", price: 5 },
      { name: "Iced Pandan Jasmine Milk Tea", group: "Tea & Juice", price: 5 },
      { name: "Honey Citron Ginger Tea", group: "Tea & Juice", price: 5 },
      { name: "Harmless Coconut Water", group: "Tea & Juice", price: 6 },
      { name: "Mango Passion Fruit Tea", group: "Tea & Juice", price: 5 },
      { name: "Strawberry Milk", group: "Tea & Juice", price: 5, soldOut: true },
      { name: "Brazil", description: "Minas Gerais 'Luzia' — natural process from the Cocatrel cooperative near Três Pontas (950–1,120 m). Caramel, chocolate, and cream with a soft, nutty finish. The same coffee we pull as espresso. 12 oz whole bean, roasted weekly.", group: "Coffee Beans by Local Coffee Roasters", price: 26, soldOut: true },
      { name: "Ethiopia", description: "Yirgacheffe Aricha — washed Kerume varietal from the Adorsi washing station (1,900–2,150 m). Floral and bright: fresh matcha, caramel, juicy mango. 12 oz whole bean, roasted weekly.", group: "Coffee Beans by Local Coffee Roasters", price: 27, soldOut: true },
      { name: "Colombia", description: "Sugarcane-process decaf that keeps its natural sweetness. 12 oz whole bean, roasted weekly.", group: "Coffee Beans by Local Coffee Roasters", price: 30, soldOut: true },
      // ADD-ONS are Toast modifier options, not standalone menu items. Prices
      // come from .modifierOptionReferences in the Toast API payload.
      { name: "Sub w/Oat Milk", group: "ADD-ONS", price: 1 },
      { name: "Sub w/Almond Milk", group: "ADD-ONS", price: 1 },
      { name: "Sub w/Coconut Milk", group: "ADD-ONS", price: 1 },
      { name: "Sweet Foam", group: "ADD-ONS", price: 1 },
      { name: "Ube Foam", group: "ADD-ONS", price: 1 },
      { name: "Matcha Foam", group: "ADD-ONS", price: 1 },
      { name: "Chocolate Foam", group: "ADD-ONS", price: 1 },
      { name: "Add Shot", group: "ADD-ONS", price: 1.5 },
    ],
  },
  {
    id: "exclusive-merch",
    label: "EXCLUSIVE MERCH",
    items: [
      { name: "TUS JEANS HAT", description: "TUSDAY jeans hat.", group: "TUS HAT", price: 25, soldOut: true },
      { name: "+bake pin", description: "+Bake logo magnetic pin.", group: "MAGNETIC PINS", price: 5 },
      { name: "heart pin", description: "Heart magnetic pin.", group: "MAGNETIC PINS", price: 5 },
      { name: "fries pin", description: "Fries and basket magnetic pin.", group: "MAGNETIC PINS", price: 5 },
      { name: "coffee pin", description: "Coffee cup magnetic pin.", group: "MAGNETIC PINS", price: 5 },
      // POS/KIOSK-only group in Toast — sold in store, not on online ordering.
      { name: "KEYCHAINS FAM FOODIE", description: "Fam foodie keychain.", group: "KEYCHAINS", price: 5 },
    ],
  },
  {
    id: "catering",
    label: "CATERING",
    items: [
      { name: "HALF TRAY OF SPINACH", group: "HALF TRAY CATERING", price: 60 },
      { name: "FULL TRAY OF TIP ME OVER", group: "FULL TRAY CATERING", price: 180 },
      { name: "FULL TRAY OF SCRAMBLED EGGS", group: "FULL TRAY CATERING", price: 180 },
    ],
  },
]
